import React, { useState } from "react";
import { Box, TextField, Button, Typography, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";

interface PartnerAccountNameFormProps {
  onSubmit: (accountName: string) => void;
  isLoading?: boolean;
  error?: string | null;
}

const PartnerAccountNameForm: React.FC<PartnerAccountNameFormProps> = ({
  onSubmit,
  isLoading = false,
  error = null,
}) => {
  const { t } = useTranslation("partnerAccountNameForm");

  const [accountName, setAccountName] = useState("");
  const [fieldError, setFieldError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!accountName.trim()) {
      setFieldError(t("errors.required"));
      return;
    }
    setFieldError("");
    onSubmit(accountName.trim());
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountName(e.target.value);
    if (fieldError) {
      setFieldError("");
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      display="flex"
      flexDirection="column"
      gap={2}
      width="100%"
      maxWidth={400}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("title")}
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {t("subtitle")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        label={t("fields.accountName.label")}
        variant="outlined"
        fullWidth
        value={accountName}
        onChange={handleChange}
        error={!!fieldError}
        helperText={fieldError}
        placeholder={t("fields.accountName.placeholder")}
        disabled={isLoading}
        autoFocus
        sx={{
          "& .MuiOutlinedInput-root": {
            fontSize: "1rem",
          },
        }}
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={isLoading}
        sx={{ mt: 2, py: 1.5 }}
      >
        {isLoading ? t("buttons.processing") : t("buttons.continue")}
      </Button>
    </Box>
  );
};

export default PartnerAccountNameForm;